/**
 * Index des composants de contenu mail
 * Chaque composant a ses métadonnées en propriété statique
 */

import type { MailComponent, MailMetadata, MailReponses } from "../../lib/types";
import type { InbasketTemplateConfig, TypeReponseMail } from "../../templates/types";

// Import de tous les composants de contenu
import AchatChaudiere from "./achat-chaudiere";
import AppelPersonnel from "./appel-personnel";
import ConfirmationDateDeReunionPlastitoc from "./confirmation-date-de-reunion-plastitoc";
import CongesAVenir from "./conges-a-venir";
import ContratsDeVente from "./contrats-de-vente";
import DemandeConges from "./demande-conges";
import DemandeFormationLicenceProfessionnelle from "./demande-formation-licence-professionnelle";
import DematerialisationDocumentationTechnique from "./dematerialisation-documentation-technique";
import DevisConstructionAtelierProduction from "./devis-construction-atelier-production";
import ExtensionDeLAtelierDeProduction from "./extension-de-l-atelier-de-production";
import HorairesDeMonsieurMurat from "./horaires-de-monsieur-murat";
import InvitationConferenceGrguNancy from "./invitation-conference-grgu-nancy";
import JourneeManagementADistance from "./journee-management-a-distance";
import LeTeletravailCaADesLimites from "./le-teletravail-ca-a-des-limites";
import LitigeSocieteRelax from "./litige-societe-relax";
import MegaConcert from "./mega-concert";
import MiseEnPlaceDemarcheRSE from "./mise-en-place-demarche-rse";
import OffreAbonnementEchos from "./offre-abonnement-echos";
import PreparationPointVisio from "./preparation-point-visio";
import ProjetDemarcheRSE from "./projet-demarche-rse";
import ProjetESytel from "./projet-e-sytel";
import ProjetInformatique from "./projet-informatique";
import ProjetRSE from "./projet-rse";
import RefusDematerialisationTechnique from "./refus-dematerialisation-documentation-technique";
import RenouvellementAbonnement from "./renouvellement-abonnement";
import RetardStructureAtelierDeProduction from "./retard-structure-atelier-de-production";
import SituationPlusQueCritique from "./situation-plus-que-critique";
import SystemeSecuriteInformatiquePassoire from "./systeme-securite-informatique-passoire";
import TeletravailServiceAdministratif from "./teletravail-service-administratif";
import UrgentErreurDeLivraison from "./urgent-erreur-de-livraison";

// Tous les composants mail
const allMailComponents: MailComponent[] = [
  AchatChaudiere,
  AppelPersonnel,
  ConfirmationDateDeReunionPlastitoc,
  CongesAVenir,
  ContratsDeVente,
  DemandeConges,
  DemandeFormationLicenceProfessionnelle,
  DematerialisationDocumentationTechnique,
  DevisConstructionAtelierProduction,
  ExtensionDeLAtelierDeProduction,
  HorairesDeMonsieurMurat,
  InvitationConferenceGrguNancy,
  JourneeManagementADistance,
  LeTeletravailCaADesLimites,
  LitigeSocieteRelax,
  MegaConcert,
  MiseEnPlaceDemarcheRSE,
  OffreAbonnementEchos,
  PreparationPointVisio,
  ProjetDemarcheRSE,
  ProjetESytel,
  ProjetInformatique,
  ProjetRSE,
  RefusDematerialisationTechnique,
  RenouvellementAbonnement,
  RetardStructureAtelierDeProduction,
  SituationPlusQueCritique,
  SystemeSecuriteInformatiquePassoire,
  TeletravailServiceAdministratif,
  UrgentErreurDeLivraison,
];

// Mapping clé mail -> composant de contenu (généré depuis metadata.cleMail)
export const mailContentComponents: Record<string, MailComponent> =
  Object.fromEntries(allMailComponents.map((c) => [c.metadata.cleMail, c]));

// Liste des mails triée par dateRelative (plus récent d'abord)
export function getAllMailsMetadata(): MailMetadata[] {
  return allMailComponents
    .map((c) => c.metadata)
    .sort((a, b) => a.dateRelative - b.dateRelative);
}

// Helper pour récupérer un composant de contenu par clé
export function getMailContentComponent(
  cleMail: string,
): MailComponent | undefined {
  return mailContentComponents[cleMail];
}

// Helper pour récupérer les métadonnées d'un mail par sa clé
export function getMailByKey(cleMail: string): MailMetadata | undefined {
  return mailContentComponents[cleMail]?.metadata;
}

// ============================================
// TEMPLATE-AWARE HELPERS
// ============================================

/**
 * Get all mails metadata filtered and ordered by template
 * @param template The template configuration
 * @returns Array of MailMetadata in template-defined order
 */
export function getMailsComponentsForTemplate(
  template: InbasketTemplateConfig,
): MailComponent[] {
  return template.mailKeys
    .map((key) => mailContentComponents[key])
    .filter((m): m is MailComponent => m !== undefined);
}

/**
 * Filtre les mails du template selon leur displayCondition.
 * Les mails sans displayCondition sont toujours visibles.
 */
export function getVisibleMails(
  templateMails: MailComponent[],
  reponses: MailReponses,
  mailTypeReponses: Record<string, TypeReponseMail> = {},
): MailComponent[] {
  return templateMails.filter(
    (m) =>
      !m.displayCondition ||
      m.displayCondition({ reponses, templateMails, mailTypeReponses }),
  );
}

/**
 * Check if a mail key exists in a template
 */
export function isMailInTemplate(
  cleMail: string,
  template: InbasketTemplateConfig,
): boolean {
  return template.mailKeys.includes(cleMail);
}
